import { FC, useState } from "react";
import ModalComponent, {
  ModalProps,
} from "../../../../../common/components/modal/modal.component";
import ButtonComponent from "../../../../../common/components/button/button.component";
import orderTranslationsHelper from "../../../../../languages/order-translations.helper";
import notificationService from "../../../../../common/utils/notification/notification.service";
import OrderDetailsCargoOrder from "../../common/types/order-details-cargo-order";
import useOrderDetailsForwarding from "../use-order-details-forwarding";
import OrderDetailsTaxiContractSelectOption from "./types/order-details-taxi-contract-select-option";
import useOrderDetailsForwardingForm from "../hooks/use-order-details-forwarding-form";
import OrderDetailsForwardingFormComponent from "./order-details-forwarding-form.component";
import OrderTransportingOrderForwardingSendParams from "../../../../../common/services/order/transporting-order/forwarding/send/order-transporting-order-forwarding-send-params";
import useAbort from "../../../../../common/hooks/use-abort";
import OrderTransportingOrderForwardingListParams from "../../../../../common/services/order/transporting-order/forwarding/list/order-transporting-order-forwarding-list-params";
import orderService from "../../../../../common/services/order/order.service";
import OrderTransportingOrderForwardingList from "../../../../../common/services/order/transporting-order/forwarding/list/order-transporting-order-forwarding-list";

type OrderDetailsForwardingModalComponentProps = Pick<
  ModalProps,
  "isOpen" | "onClose"
> & {
  details: OrderDetailsCargoOrder | null;
};

const OrderDetailsForwardingModalComponent: FC<
  OrderDetailsForwardingModalComponentProps
> = ({ isOpen, details, onClose }) => {
  const [contractId, setContractId] = useState("");
  const [selectedTaxiCorporationId, setSelectedTaxiCorporationId] =
    useState("");
  const [isLoading, setIsLoading] = useState(false);
  const {
    formValidationResults,
    setTaxiCorporation,
    setTaxiContract,
    checkIsFormValid,
  } = useOrderDetailsForwardingForm({});

  const { transportingOrderUuid: transportingOrderId, taxiCorporationUuid } =
    details!;

  const { consumerTaxiId, forwardingId, setForwardingId } =
    useOrderDetailsForwarding({
      transportingOrderId,
    });

  const translations =
    orderTranslationsHelper.getDetailsTranslations().forwarding;

  const transportingOrderSendForwardingSignal = useAbort();
  const transportingOrderFetchForwardingSignal = useAbort();

  const {
    form,
    headingText,
    successForwardingNotificationText,
    failureForwardingNotificationText,
  } = translations;
  const {
    submitButtonTitle,
    submitButtonText,
    submitDisabledButtonTitle,
    rejectButtonTitle,
    rejectButtonText,
  } = form;

  const submitText = !!forwardingId
    ? submitDisabledButtonTitle
    : submitButtonTitle;

  const handleTaxiContractChange = (
    option: OrderDetailsTaxiContractSelectOption | null,
  ) => {
    setContractId(option?.value?.id!);
    setTaxiContract(option);
  };

  const getForwardings = () => {
    if (!transportingOrderId) {
      return;
    }

    const loadParams: OrderTransportingOrderForwardingListParams = {
      transportingOrderId: transportingOrderId,
    };

    orderService
      .getForwardingList(
        loadParams,
        transportingOrderFetchForwardingSignal.signal,
      )
      .then(handleForwardingResponse);
  };

  const handleForwardingResponse = (
    response: OrderTransportingOrderForwardingList,
  ) => {
    setForwardingId(response?.data[0]?.id);
  };

  const handleForwardingSubmitSuccess = () => {
    notificationService.success(successForwardingNotificationText);
    onClose();
  };

  const handleForwardingSubmitFailure = () => {
    notificationService.error(failureForwardingNotificationText);
  };

  const handleOnSubmit = () => {
    const isFormValid = checkIsFormValid();
    if (!isFormValid) return;

    const params: OrderTransportingOrderForwardingSendParams = {
      contractId: contractId,
      orderId: transportingOrderId,
    };

    orderService
      .sendForwarding(params, transportingOrderSendForwardingSignal.signal)
      .then(handleForwardingSubmitSuccess)
      .catch(handleForwardingSubmitFailure)
      .finally(() => setIsLoading(false));
  };

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      classNames={{
        content: `modal_content`,
      }}
      header={{
        title: headingText,
      }}
      actions={[
        <ButtonComponent
          onClick={handleOnSubmit}
          type="primary"
          title={submitText}
          isLoading={isLoading}
        >
          {submitButtonText}
        </ButtonComponent>,
        <ButtonComponent
          type="brand"
          onClick={onClose}
          title={rejectButtonTitle}
        >
          {rejectButtonText}
        </ButtonComponent>,
      ]}
    >
      <OrderDetailsForwardingFormComponent
        producerTaxiId={forwardingId ? consumerTaxiId : taxiCorporationUuid}
        transportingOrderId={transportingOrderId}
        forwardingId={forwardingId}
        formValidationResults={formValidationResults}
        selectedTaxiCorporationId={selectedTaxiCorporationId}
        setSelectedTaxiCorporationId={setSelectedTaxiCorporationId}
        getForwardings={getForwardings}
        handleTaxiContractChange={handleTaxiContractChange}
        setTaxiCorporation={setTaxiCorporation}
      />
    </ModalComponent>
  );
};

export default OrderDetailsForwardingModalComponent;
